import Vue from "vue";
import axios from "axios";
import store from "@/stores";
import i18n from "@/plugins/i18n";
import Swal from "sweetalert2";
import {
  invalidateForMutation,
  markMutation,
  getCacheRule,
  cachedRequest,
  scopeOf,
} from "@/utils/fetchCache";

// get host url from window
const baseURL = process.env.VUE_APP_BASE_URL;

// create axios instance
const service = axios.create({
  baseURL: `${baseURL}/api/`,
  timeout: 30000, // 30 seconds timeout
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    "X-Requested-With": "XMLHttpRequest",
  },
});

// request interceptor
service.interceptors.request.use((config) => {
  const isLoggedIn = store.getters["auth/isLoggedIn"];
  // if token exists, add it to the header
  if (isLoggedIn) {
    const token = store.getters["auth/token"];
    config.headers["X-Token"] = token;
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  // check token expiration
  return config;
});

// errorAlert function definition
const errorAlert = async (message) => {
  // swal.fire
  const _title = i18n.t("failed");
  await Swal.fire({
    icon: "error",
    title: _title,
    text: message,
    showConfirmButton: false,
  });
};

// handle error
const handleError = (error) => {
  if (error.response) {
    console.error("[http error]", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response.status,
      data: error.response.data,
    });
  } else {
    console.error("[http error]", {
      message: error.message,
      noResponse: true,
    });
  }
  // check if error has response
  if (error.response) {
    // check if error response has data
    if (error.response.data) {
      // check if error response data has message
      if (error.response.data.message) {
        // show error message
        const message = error.response.data.message;
        // check if error message is an object
        if (typeof message === "object") {
          // For validation errors, show only the first error message
          const firstKey = Object.keys(message)[0];
          const firstError = message[firstKey];
          if (Array.isArray(firstError) && firstError.length > 0) {
            const msg = i18n.t(firstError[0]);
            errorAlert(msg);
          }
        } else {
          const msg = i18n.t(message);
          errorAlert(msg);
        }
        // return error message
        return Promise.reject(error.response.data);
      }
    }
  }
};

// first branch_expired response moves the app to /manage-branches once; no popups.
// branchExpiredSeen lasts until the next full page load (switching branch reloads the page)
const BRANCH_EXPIRED_TARGET = "/manage-branches";
let redirectingToBranchExpired = false;
let branchExpiredSeen = false;
export const isBranchExpiredSeen = () => branchExpiredSeen;
const goToBranchExpired = () => {
  branchExpiredSeen = true;
  if (redirectingToBranchExpired) return;
  // lazy require avoids a circular import (router -> pages -> plugins)
  const router = require("@/router").default;
  if (router.currentRoute.path === BRANCH_EXPIRED_TARGET) return;
  redirectingToBranchExpired = true;
  router
    .push(BRANCH_EXPIRED_TARGET)
    .catch(() => {})
    .finally(() => {
      redirectingToBranchExpired = false;
    });
};

// response interceptor
service.interceptors.response.use(
  (res) => {
    // a successful write makes cached reference lists (categories, units, ...) stale
    const method = (res.config && res.config.method) || "get";
    if (method !== "get") {
      invalidateForMutation(res.config.url);
      markMutation();
    }
    return res;
  },
  (error) => {
    const data = error.response?.data;
    // Check if this is a permission check request (has_role)
    const isPermissionCheck = error.config?.url?.includes("/has_role");
    const isLogout = error.config?.url?.includes("logout");

    if (isLogout) {
      return Promise.reject(error);
    }

    if (data && data.message === "branch_expired") {
      goToBranchExpired();
      return Promise.reject(error);
    }

    if (data && data.code === 401) {
      // Use SweetAlert for 401 errors instead of toast
      Swal.fire({
        icon: "error",
        title: i18n.t("session_expired") || "Session Expired",
        text: data.message || "Your session has expired. Please login again.",
        showConfirmButton: true,
        confirmButtonText: i18n.t("ok") || "OK",
      });
      store.dispatch("auth/removeAuth");
      window.location.href = "/login";
    }
    // Skip error alerts for permission checks (400 errors from has_role)
    if (isPermissionCheck && error.response && error.response.status === 400) {
      return Promise.reject(error);
    }
    // Skip error alerts for 404 errors - let component handle with toast
    if (error.response && error.response.status === 404) {
      return Promise.reject(error);
    }
    // handle error
    return handleError(error);
  }
);

// Short-lived GET cache for a few list endpoints (see GET_RULES in utils/fetchCache.js).
// Requests with cancel tokens / signals / non-JSON responses are never cached.
const rawGet = service.get.bind(service);
service.get = (url, config) => {
  const rule = getCacheRule(url);
  if (
    !rule ||
    (config && (config.signal || config.cancelToken || config.responseType))
  ) {
    return rawGet(url, config);
  }
  const scope = scopeOf(
    store.getters["auth/token"],
    store.getters["auth/user"]
  );
  return cachedRequest(scope, url, config, rule, () => rawGet(url, config));
};

const AxiosPlugin = {
  install(Vue) {
    Vue.prototype.$http = service;
    Vue.prototype.$axios = service;
  },
};
// export axios instance
export default AxiosPlugin;
